// /src/components/chat/ChatWidget.js
import React, { useState } from 'react';
import { ChatFormMessage } from './shared/MessagesBox';
import ChatRatingDialog from './ChatRatingDialog';
import { Box, IconButton, Button, styled, Snackbar, FormControlLabel, Checkbox, TextField } from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ChatHeader from './shared/ChatHeader';
import MessagesBox from './shared/MessagesBox';
import InputContainer from './shared/InputContainer';
import { getCurrentSessionId, endChatSession, trackChatActivity } from '../../services/chatSessionService';
import axios from 'axios';
import Cookies from '../../common/Cookies';
import PoweredByChattitude from '../common/PoweredByChattitude';
import CalcomBookerDialog from './CalcomBookerDialog';
import IntermediateWelcomePanel from './IntermediateWelcomePanel';
import { getIntermediateConfig, isWelcomeStepActive } from './intermediateWelcome';

// Styled components to match the original ChatPage.js
const ChatContainer = styled(Box)(({ height, width }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: `${height}px`,
  width: `${width}px`,
  borderRadius: '12px',
  backgroundColor: 'white',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

const ChatWidget = ({
  customization,
  messages,
  setMessages,
  isChatClosed,
  setIsChatClosed,
  ws,
  setWs,
  isConnected,
  setIsConnected,
  devicePreview,
  currentBotMessageRef,
  soundEnabledRef,
  isMutedRef,
  isMuted,
  setIsMuted,
  sessionId,
  onEndSession, // Add this prop to handle session ending from ChatMain
  tenantId,
  agentId, // Add agentId prop
  initialMessageSent, // From ChatMain
  setInitialMessageSent, // From ChatMain
  closeConfirmText, // Add this prop for the close confirmation modal text
  showCloseConfirm,
  setShowCloseConfirm,
  setCloseConfirmText,
  loading, // Loading state for disabling send button
  setLoading, // Setter for loading state
  calcomWidget,
  setCalcomWidget
}) => {
  // Track if user sent at least one message (not greeting)
  const [userSentMessage, setUserSentMessage] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  // Local state to allow user to close the automated/minimized agent bubble
  const [showAgentBubble, setShowAgentBubble] = useState(true);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingSessionId, setRatingSessionId] = useState(null);
  const [requestTranscript, setRequestTranscript] = useState(false);
  const [visitorEmail, setVisitorEmail] = useState('');
  const [visitorEmailError, setVisitorEmailError] = useState('');
  const [sendingTranscript, setSendingTranscript] = useState(false);
  // Remove local initialMessageSent, use prop from ChatMain
  const [fontSize, setFontSize] = useState(customization.fontSize || 'medium');
  // showCloseConfirm and closeConfirmText now come from props (ChatMain)

  // Use initialMessageSent and setInitialMessageSent from props (ChatMain)
  // (Assume these are passed as props, as in your ChatMain.js)
  // Remove all usage of local initialMessageSent state

  const handleSend = (text, opts = {}) => {
    // If close confirmation modal is open, do not send or add waiting message
    if (showCloseConfirm) {
      return;
    }

    console.log('🔍 DEBUG handleSend - agentId:', agentId, 'tenantId:', tenantId);

    // Track chat activity immediately when sending message (includes starter prompts)
    console.log('🔄 ChatWidget: Tracking chat activity for message send');
    trackChatActivity();

    const userMessage = {
      text,
      displayText: opts.displayMessage || undefined,
      isBot: false,
      timestamp: new Date()
    };
    setMessages((prev) => [...prev, userMessage]);

    if (typeof setInitialMessageSent === 'function') {
      setInitialMessageSent(true);
    }

    setUserSentMessage(true); // Mark that user sent a message
    // Play sent sound if enabled
    if (customization.soundEnabled && !isMutedRef.current) {
      playSound('sent');
    }

    // Set loading to true to disable send button while waiting for response
    if (typeof setLoading === 'function') {
      setLoading(true);
    }

    if (ws && ws.readyState === WebSocket.OPEN) {
      // Add waiting message with agent name if available

      let waitingText = 'Thinking';
      if (customization.agentName && customization.waitingMessage) {
        waitingText = `${customization.agentName} ${customization.waitingMessage}`;
      } else if (customization.waitingMessage) {
        waitingText = customization.waitingMessage;
      } else if (customization.agentName) {
        waitingText = `${customization.agentName} is thinking`;
      }

      const waitingMessage = {
        text: waitingText,
        isBot: true,
        timestamp: new Date(),
        isWaiting: true
      };

      setMessages(prev => [...prev, waitingMessage]);

      // Reset the current bot message ref when sending a new message
      currentBotMessageRef.current = null;

      // Get the session ID from ChatMain's sessionId prop (internal sessions)
      const currentSessionId = sessionId;
      console.log('Sending message with sessionId:', currentSessionId);

      const messagePayload = {
        action: "chat",
        message: text,
        agentId: agentId,
        tenantId: tenantId,
        model: customization.model || '1',
        sessionId: currentSessionId,
        instructions: "",
        ...opts // displayMessage, triggerForm, etc.
      };

      console.log('🔍 DEBUG message payload:', JSON.stringify(messagePayload));

      // Format message exactly like ChatPage.js
      ws.send(JSON.stringify(messagePayload));
    }
  };

  // Allow agent/orchestration to trigger form display
  const triggerForm = () => {
    handleSend('', { triggerForm: true });
  };

  // Helper functions should be at the top level of ChatWidget, not inside handleSend
  const playSound = (type) => {
    if (!customization.soundEnabled || isMutedRef.current) {
      return;
    }

    const soundFile = type === 'sent'
      ? '/sounds/sent_default.wav'
      : '/sounds/rec_default.wav';

    try {
      const audio = new Audio(soundFile);
      audio.volume = 0.5;
      audio.play().catch(err => console.error('Error playing sound:', err));
    } catch (error) {
      console.error('Error creating audio:', error);
    }
  };

  const cycleFontSize = () => {
    if (fontSize === 'medium') {
      setFontSize('large');
    } else if (fontSize === 'large') {
      setFontSize('x-large');
    } else {
      setFontSize('medium');
    }
  };

  const getWidgetSize = () => {
    if (isMinimized) return { width: '90vw', height: '90vh' };
    if (devicePreview === 'mobile') return { width: 320, height: Math.min(600, customization.chatHeight || 600) };
    if (devicePreview === 'tablet') return { width: 768, height: customization.chatHeight || 600 };
    return { width: customization.chatWidth || 400, height: customization.chatHeight || 600 };
  };

  const chatIconShape = customization.chatIconShape || 'round';
  const chatIconSurface = customization.chatIconSurface || 'solid';
  const showChatIconShadow = customization.chatIconShadow !== false;
  const chatIconBorderRadius = chatIconShape === 'square' ? '0' : '50%';
  const hasCustomChatIcon = Boolean(customization.chatIcon);

  // Helper: get device preview style
  const getDevicePreviewStyle = () => {
    const intermediate = getIntermediateConfig(customization);
    const borderWidth = devicePreview === 'desktop' ? 1 : 12;
    const borderRadius = devicePreview === 'desktop' ? 12 : 20;
    const borderColor = devicePreview === 'desktop' ? '#E8E8E8' : '#333';
    const bgColor = devicePreview === 'desktop' ? 'white' : '#333';
    const frameWidth = showWelcome
      ? intermediate.width
      : (devicePreview === 'mobile' ? 320 : devicePreview === 'tablet' ? 768 : customization.chatWidth || 400);
    const frameHeight = showWelcome
      ? intermediate.height
      : (devicePreview === 'mobile' ? Math.min(600, customization.chatHeight || 600) : devicePreview === 'tablet' ? customization.chatHeight || 600 : customization.chatHeight || 600);
    return {
      width: frameWidth,
      height: frameHeight,
      border: `${borderWidth}px solid ${borderColor}`,
      borderRadius: `${borderRadius}px`,
      backgroundColor: bgColor,
      boxShadow: devicePreview === 'desktop' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 5px 15px rgba(0,0,0,0.3)',
      position: 'relative',
      display: 'flex',
      alignItems: showWelcome ? 'stretch' : 'flex-end',
      justifyContent: showWelcome ? 'flex-start' : 'center',
      transition: 'all 0.3s ease',
      overflow: 'hidden',
    };
  };

  const widgetStyle = getWidgetSize();

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const toggleMute = () => {
    if (setIsMuted) {
      setIsMuted(!isMuted);
    }
    isMutedRef.current = !isMutedRef.current;
  };

  // Handler to minimize chat (does NOT close session)
  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleStartConversation = () => {
    setConversationStarted(true);
    setIsMinimized(false);
  };

  const showWelcome = isWelcomeStepActive(customization) && !conversationStarted && !isMinimized && !isChatClosed;
  // Handler to restore chat from minimized or closed state
  const handleRestore = async () => {
    if (isChatClosed) {
      // Fix: Reset idle detection state so idle timer restarts on reopen after idle timeout
      window._justReopenedChat = true;
      setIsChatClosed(false);
      // Do NOT clear messages - let them persist like real widget
    }
    setIsMinimized(false);
  };
  // Handler to close chat (show confirmation dialog)
  const handleClose = () => {
    setRequestTranscript(false);
    setVisitorEmail('');
    setVisitorEmailError('');
    setShowCloseConfirm(true);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submitVisitorTranscriptRequest = async () => {
    if (!requestTranscript) {
      return true;
    }

    const trimmedEmail = visitorEmail.trim();
    if (!trimmedEmail) {
      setVisitorEmailError('Email is required.');
      return false;
    }
    if (!isValidEmail(trimmedEmail)) {
      setVisitorEmailError('Please enter a valid email address.');
      return false;
    }

    try {
      setSendingTranscript(true);
      await axios.post('/chat/transcript-email', {
        tenantId,
        agentId,
        sessionId,
        email: trimmedEmail,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
        }
      });
      setVisitorEmailError('');
      return true;
    } catch (err) {
      console.error('Failed to send visitor transcript email:', err);
      const apiMsg = err?.response?.data?.message || err?.response?.data;
      setVisitorEmailError(typeof apiMsg === 'string' ? apiMsg : 'Unable to send transcript email. Please try again.');
      return false;
    } finally {
      setSendingTranscript(false);
    }
  };

  // Handler for confirming end chat
  const handleEndChat = async () => {
    const transcriptOk = await submitVisitorTranscriptRequest();
    if (!transcriptOk) {
      return;
    }

    // Capture sessionId before ending session
    setRatingSessionId(sessionId);

    // End session as usual
    if (onEndSession) {
      await onEndSession('closed');
    } else {
      await endChatSession('closed');
    }
    setShowCloseConfirm(false);
    setRequestTranscript(false);
    setVisitorEmail('');
    setVisitorEmailError('');
    // Only show rating dialog if user sent a message
    if (customization.chatRatingEnabled !== false && userSentMessage) {
      setShowRatingDialog(true);
    } else {
      setIsMinimized(true);
      if (typeof setIsChatClosed === 'function') setIsChatClosed(true);
    }
  };
  // Handler for cancelling close
  const handleCancelClose = () => {
    setShowCloseConfirm(false);
    setRequestTranscript(false);
    setVisitorEmail('');
    setVisitorEmailError('');
    // Remove any 'thinking' (waiting) message from the end of the chat
    setMessages(prev => {
      let updated = prev;
      if (updated.length === 0) return updated;
      // Remove last message if it's a waiting message
      if (updated[updated.length - 1].isWaiting) {
        updated = updated.slice(0, -1);
      }
      // Remove any form message (assume only one form at a time)
      updated = updated.filter(m => !m.isForm);
      // Add the custom success message as a chat bubble if not already present at the end
      const lastMsg = updated[updated.length - 1];
      if (!lastMsg || lastMsg.text !== closeConfirmText) {
        updated = [
          ...updated,
          {
            text: closeConfirmText || 'Thank you, a human will be in touch with you soon.',
            isBot: true,
            timestamp: new Date()
          }
        ];
      }
      return updated;
    });
  };

  // Handle form submission: show close modal with custom message instead of chat bubble
  const handleFormSubmit = (values, fields) => {
    // Validate email if present
    const emailField = Array.isArray(fields) ? fields.find(f => f.type === 'email') : null;
    if (emailField) {
      const email = values[emailField.name];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert('Please enter a valid email address.');
        return false;
      }
    }
    // Find the last form message to get the success message
    const lastFormMsg = [...messages].reverse().find(msg => msg.isForm && msg.successMessage !== undefined);
    let successMessage = 'Thank you for submitting your information!';
    if (lastFormMsg && lastFormMsg.successMessage && lastFormMsg.successMessage.trim() !== '') {
      successMessage = lastFormMsg.successMessage;
    }
    // Optionally: send form data to server via WebSocket
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'formSubmission', data: values }));
    }
    // Show close modal with the form's custom message
    if (typeof setCloseConfirmText === 'function') {
      setCloseConfirmText(successMessage);
    }
    setShowCloseConfirm(true);
    return true;
  };

  // const ChatFormMessage = ({ fields, onSubmit }) => {
  //   const [values, setValues] = useState(() =>
  //     fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {})
  //   );
  //   const [error, setError] = useState('');

  //   const handleChange = e => {
  //     setValues({ ...values, [e.target.name]: e.target.value });
  //   };

  //   const handleSubmit = e => {
  //     e.preventDefault();
  //     const emailField = fields.find(f => f.type === 'email');
  //     if (emailField) {
  //       const email = values[emailField.name];
  //       const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //       if (!emailRegex.test(email)) {
  //         setError('Please enter a valid email address.');
  //         return;
  //       }
  //     }
  //     setError('');
  //     onSubmit(values);
  //   };

  //   return (
  //     <form onSubmit={handleSubmit} style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, margin: '8px 0', maxWidth: 420 }}>
  //       <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
  //         <tbody>
  //           {fields.map(f => (
  //             <tr key={f.name} style={{ border: '1px solid #ccc' }}>
  //               <td style={{
  //                 width: 140,
  //                 textAlign: 'right',
  //                 fontWeight: 500,
  //                 padding: '8px',
  //                 border: '1px solid #ccc',
  //                 verticalAlign: 'middle'
  //               }}>
  //                 <label htmlFor={f.name} style={{ width: '100%' }}>
  //                   {f.label}
  //                   {f.required && <span style={{ color: 'red', marginLeft: 4, fontSize: '14px', verticalAlign: 'super' }}>*</span>}
  //                 </label>
  //               </td>
  //               <td style={{
  //                 padding: '8px',
  //                 border: '1px solid #ccc',
  //                 textAlign: 'left',
  //                 verticalAlign: 'middle'
  //               }}>
  //                 {f.type === 'textarea' ? (
  //                   <textarea
  //                     id={f.name}
  //                     name={f.name}
  //                     value={values[f.name]}
  //                     onChange={handleChange}
  //                     required={f.required}
  //                     rows={f.rows || 3}
  //                     style={{
  //                       width: '100%',
  //                       minWidth: 180,
  //                       padding: 8,
  //                       borderRadius: 4,
  //                       border: '1px solid #ccc',
  //                       resize: 'vertical',
  //                       textAlign: 'left'
  //                     }}
  //                   />
  //                 ) : (
  //                   <input
  //                     id={f.name}
  //                     type={f.type}
  //                     name={f.name}
  //                     value={values[f.name]}
  //                     onChange={handleChange}
  //                     required={f.required}
  //                     style={{
  //                       width: '100%',
  //                       minWidth: 180,
  //                       padding: 8,
  //                       borderRadius: 4,
  //                       border: '1px solid #ccc',
  //                       textAlign: 'left'
  //                     }}
  //                   />
  //                 )}
  //               </td>
  //             </tr>
  //           ))}
  //         </tbody>
  //       </table>
  //       {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
  //       <button type="submit" style={{ marginTop: 8 }}>Submit</button>
  //     </form>
  //   );
  // };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-start',
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <Box sx={getDevicePreviewStyle()}>
        {/* Device preview always visible */}
        {isMinimized || isChatClosed ? (
          <>
            {/* Chat bubble above minimized icon, supports manual or automated closed-agent text */}
            {showAgentBubble && customization.showBubble && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 85, // position above the icon
                  right: 15,
                  background: 'white',
                  color: '#333',
                  borderRadius: '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  padding: '12px 18px',
                  zIndex: 1001,
                  fontSize: '16px',
                  maxWidth: 260,
                  textAlign: 'left',
                  border: '1px solid #eee',
                }}
              >
                {/* Close button for the bubble (does not affect the minimized icon) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAgentBubble(false);
                  }}
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '-10px',
                    background: '#fff',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    transition: 'all 0.2s',
                    zIndex: 10,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
                    e.target.style.transform = 'scale(1)';
                  }}
                  aria-label="Close chat bubble"
                >
                  <span style={{
                    fontSize: '20px',
                    color: '#333',
                    lineHeight: 1,
                    fontWeight: 'bold'
                  }}>×</span>
                </button>
                {/* Wrap text at word boundaries, max 25 chars per line, max 5 lines */}
                {(() => {
                  // Word wrap helper
                  function wordWrap(str, maxLen) {
                    const result = [];
                    str.split(/\r?\n/).forEach(rawLine => {
                      let line = '';
                      rawLine.split(' ').forEach(word => {
                        if ((line + word).length > maxLen) {
                          if (line) result.push(line.trim());
                          line = word + ' ';
                        } else {
                          line += word + ' ';
                        }
                      });
                      if (line.trim()) result.push(line.trim());
                    });
                    return result;
                  }
                  // Compute bubble text (automated or manual)
                  const computeBubbleText = (() => {
                    // helper: get host page title if provided by embedding page or parent
                    const getHostPageTitle = () => {
                      try {
                        // Prefer config on parent if the embed script passed it
                        if (window && window.parent && window.parent.slickbotsConfig && window.parent.slickbotsConfig.pageTitle) {
                          return String(window.parent.slickbotsConfig.pageTitle || '').trim();
                        }
                      } catch (e) {
                        // cross-origin access will throw; ignore
                      }
                      try {
                        // If same-origin, try parent document title
                        if (window && window.parent && window.parent.document && window.parent.document.title) {
                          return String(window.parent.document.title || '').trim();
                        }
                      } catch (e) {
                        // ignore
                      }
                      // fallback to our own document.title
                      return (typeof document !== 'undefined' && document.title) ? String(document.title).trim() : '';
                    };

                    const rawTitle = getHostPageTitle();
                    const safeTitle = rawTitle.replace(/</g, '&lt;').replace(/>/g, '&gt;');

                    // Check bubbleType to determine which text to show
                    const bubbleType = customization.bubbleType || 'none';

                    // Automated (Predefined Phrases) -> pick a random phrase and substitute token
                    if (bubbleType === 'automated') {
                      let phrases = customization.automatedClosedAgentPhrases;
                      if (typeof phrases === 'string') {
                        phrases = phrases.split(/\r?\n/);
                      }
                      phrases = Array.isArray(phrases) ? phrases.map(p => String(p || '').trim()).filter(Boolean) : [];
                      const picked = phrases[Math.floor(Math.random() * phrases.length)];
                      const replaced = picked.replace(/\{\{pageTitle\}\}/g, safeTitle || 'this page');
                      return replaced;
                    }

                    // AI-based bubble -> use AI-generated nudge based on page URL (handled server-side)
                    if (bubbleType === 'aiBased') {
                      // AI-based nudges are matched server-side based on URL
                      // Return empty string here, as the actual nudge will be determined server-side
                      // Or use fallback nudge if configured
                      if (customization.fallbackNudge) {
                        return customization.fallbackNudge;
                      }
                      return '';
                    }
                  })();
                  const text = computeBubbleText;
                  const lines = wordWrap(text, 25);
                  return lines.slice(0, 5).map((line, idx) => (
                    <div key={idx} style={{ marginBottom: 2 }}>{line}</div>
                  ));
                })()}
              </Box>
            )}
            {/* Minimized chat icon */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 5,
                right: 5,
                width: devicePreview === 'mobile' ? '50px' : devicePreview === 'tablet' ? '60px' : '60px',
                height: devicePreview === 'mobile' ? '50px' : devicePreview === 'tablet' ? '60px' : '60px',
                borderRadius: chatIconBorderRadius,
                // Preserve transparency for uploaded icons regardless of surface mode.
                backgroundColor: hasCustomChatIcon
                  ? 'transparent'
                  : (chatIconSurface === 'floating' ? 'transparent' : customization.headerBackgroundColor),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                boxShadow: showChatIconShadow
                  ? (chatIconSurface === 'floating'
                      ? '0 6px 16px rgba(0, 0, 0, 0.22)'
                      : '0 2px 10px rgba(0, 0, 0, 0.2)')
                  : 'none',
                zIndex: 1000,
                transition: 'transform 0.3s ease',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'scale(1.1)',
                }
              }}
              onClick={handleRestore}
            >
              {customization.chatIcon ? (
                <img
                  src={customization.chatIcon}
                  alt="Chat"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: chatIconBorderRadius,
                    display: 'block'
                  }}
                />
              ) : (
                <span style={{
                  fontSize: devicePreview === 'mobile' ? '24px' : '30px',
                  color: 'white'
                }}>💬</span>
              )}
            </Box>
          </>
        ) : showWelcome ? (
          <Box sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <IntermediateWelcomePanel
              customization={customization}
              onMinimize={handleMinimize}
              onStart={handleStartConversation}
            />
          </Box>
        ) : (
          <ChatContainer
            height={widgetStyle.height}
            width={widgetStyle.width}
            sx={{
              borderRadius: 0, // Remove inner border radius so header aligns with device border
              boxShadow: 'none', // Remove inner shadow
              backgroundColor: 'transparent', // Let device preview bg show at edges
              overflow: 'hidden',
            }}
          >
            <ChatHeader
              customization={customization}
              isMutedRef={isMutedRef}
              soundEnabledRef={soundEnabledRef}
              onToggleMute={toggleMute}
              onToggleMinimize={handleMinimize}
              onToggleFontSize={cycleFontSize}
              onClose={handleClose}
              isMuted={isMuted}
              setIsMuted={setIsMuted}
              devicePreview={devicePreview} // pass devicePreview prop
            />
            <MessagesBox
              messages={messages}
              customization={customization || {}}
              fontSize={fontSize}
              ws={ws}
              setMessages={setMessages}
              onSubmit={handleFormSubmit}
              onSendMessage={handleSend}
            />
            {/* Form rendering is handled inside MessagesBox; do not render ChatFormMessage here. */}
            {/* Preset response buttons */}
            {customization.presetResponses?.some(p => p.active) && !initialMessageSent && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 1,
                  p: 1,
                  backgroundColor: customization.footerBackgroundColor,
                  borderTop: '1px solid #E8E8E8',
                }}
              >
                {customization.presetResponses
                  .filter(preset => preset.active && preset.text.trim())
                  .map(preset => (
                    <Button
                      key={preset.id}
                      variant="outlined"
                      size="small"
                      onClick={() => handleSend(preset.text)}
                      sx={{
                        borderRadius: '16px',
                        textTransform: 'none',
                        minHeight: '32px',
                        py: 0.5,
                        px: 1.5,
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        overflow: 'hidden',
                        color: customization.starterPromptTextColor || 'var(--primary-color, #1976d2)',
                        borderColor: customization.starterPromptBorderColor || 'var(--primary-color, #1976d2)',
                        backgroundColor: customization.starterPromptBackgroundColor || '#FFFFFF',
                        '&:hover': {
                          backgroundColor: customization.starterPromptBackgroundColor || '#FFFFFF',
                          opacity: 0.9,
                        },
                      }}
                    >
                      {preset.text}
                    </Button>
                  ))}
              </Box>
            )}
            <InputContainer customization={customization} handleSend={handleSend} loading={loading} />
            {/* Disclaimer at the bottom of the chat preview */}
            {customization.disclaimerText && (
              <Box
                sx={{
                  fontSize: '12px',
                  color: 'text.secondary',
                  textAlign: customization.disclaimerAlign || 'left',
                  mt: 1,
                  mb: 1,
                  px: 2,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-line',
                  width: '100%'
                }}
              >
                {customization.disclaimerText}
              </Box>
            )}
            <PoweredByChattitude
              tenantId={tenantId}
              poweredByLogoUrl={customization?.poweredByLogoUrl}
              poweredByLogoHref={customization?.poweredByLogoHref}
            />
          </ChatContainer>
        )}
        {/* Close confirmation dialog overlay */}
        {showCloseConfirm && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0,0,0,0.3)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 3,
                p: 4,
                minWidth: 280,
                maxWidth: 340,
                textAlign: 'center',
              }}
            >
              <Box sx={{ mb: 2, fontWeight: 500, fontSize: 18 }}>
                {closeConfirmText || 'Thanks for chatting with us today!'}
              </Box>
              {customization.visitorTranscriptEnabled === true && (
                <Box sx={{ mb: 2, textAlign: 'left' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={requestTranscript}
                        onChange={(e) => {
                          setRequestTranscript(e.target.checked);
                          if (!e.target.checked) {
                            setVisitorEmailError('');
                          }
                        }}
                        size="small"
                      />
                    }
                    label="Email me this transcript"
                    sx={{ m: 0, mb: 1 }}
                  />
                  {requestTranscript && (
                    <TextField
                      fullWidth
                      size="small"
                      label="Email Address"
                      value={visitorEmail}
                      onChange={(e) => {
                        setVisitorEmail(e.target.value);
                        if (visitorEmailError) {
                          setVisitorEmailError('');
                        }
                      }}
                      error={!!visitorEmailError}
                      helperText={visitorEmailError || 'We will email your transcript after chat ends.'}
                      placeholder="name@example.com"
                    />
                  )}
                </Box>
              )}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mb: 2, fontWeight: 600 }}
                onClick={handleEndChat}
                disabled={sendingTranscript}
              >
                {sendingTranscript ? 'Sending...' : 'End chat'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={handleCancelClose}
                disabled={sendingTranscript}
              >
                Continue chat
              </Button>
            </Box>
          </Box>
        )}
        {/* Chat Rating Dialog after close confirmation */}
        {calcomWidget?.open && calcomWidget?.username && calcomWidget?.eventSlug && (
          <CalcomBookerDialog
            open={calcomWidget.open}
            username={calcomWidget.username}
            eventSlug={calcomWidget.eventSlug}
            onClose={() => {
              if (typeof setCalcomWidget === 'function') {
                setCalcomWidget((prev) => ({ ...(prev || {}), open: false }));
              }
            }}
          />
        )}
        {showRatingDialog && (
          <ChatRatingDialog
            open={showRatingDialog}
            prompt={customization.chatRatingPrompt || 'How would you rate your chat experience?'}
            thankYou={customization.chatRatingThankYou || 'Thank you for your feedback!'}
            allowComments={customization.chatRatingAllowComments === true}
            ratingSubmitted={ratingSubmitted}
            onSubmit={async (rating, comment) => {
              console.log('Session ID for rating:', ratingSessionId);
              try {
                await axios.post('/chat/rating', {
                  tenantId: tenantId,
                  sessionId: ratingSessionId,
                  agentId,
                  rating,
                  comment,
                }, {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                  }
                });
              } catch (err) {
                console.error('Failed to submit chat rating:', err);
              }
              setRatingSubmitted(true);
            }}
            onCancel={() => {
              setShowRatingDialog(false);
              setIsMinimized(true);
              if (typeof setIsChatClosed === 'function') setIsChatClosed(true);
            }}
            onThankYouTimeout={() => {
              setShowRatingDialog(false);
              setIsMinimized(true);
              if (typeof setIsChatClosed === 'function') setIsChatClosed(true);
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default ChatWidget;